/**
 * @file Especialização Date do pipeline Input.
 * @module components/create/date
 *
 * Campo de data textual com máscara, calendário próprio, limites, destaque de
 * feriados e navegação automática entre campos.
 *
 * @see module:components/create/input
 */

import Format from "../../tools/format";
import { applyFullDefault } from './fullDefaults';

/**
 * Implementação do Date e do calendário associado.
 *
 * @class
 */
class DateClass {

  /**
   * Configura um input de data EP.
   *
   * O input HTML é textual e recebe máscara `date`. Ao focar, abre o calendário
   * dentro do Dialog mais próximo ou no `body`. Blur remove o calendário e
   * valida a data; ao completar 10 caracteres ou pressionar Tab/Enter, o foco é
   * transferido para o próximo campo elegível.
   *
   * @param {HTMLInputElement} elem Input base.
   * @param {Object} options Opções recebidas pelo componente.
   * @param {string|Date} [options.value] Data inicial; preservada em `data.value` e formatada para exibição.
   * @param {string|null} [options.min] Data mínima em `YYYY-MM-DD`. Ausente: 1º de janeiro de 10 anos antes do ano atual. `null`: sem limite inferior.
   * @param {string|null} [options.max] Data máxima em `YYYY-MM-DD`. Ausente: 31 de dezembro de 10 anos após o ano atual. `null`: sem limite superior.
   * @returns {Array.<HTMLInputElement|Object>} Input e o mesmo objeto `options` normalizado.
   * @see module:components/create/input
   */
  date(elem, options) {
    const format = new Format();
    options.value = options.forcedValue ?? options.value ?? options.defaultValue;
    applyFullDefault(options);

    if (!options.data) {
      options.data = {};
    }

    if (options.value) {
      options.data.value = options.value;
      options.value = format.date(options.value).replace(/[^0-9]/g, '');
      if (options.forcedValue !== undefined) options.forcedValue = options.value;
    }

    if (!Object.hasOwn(options, 'min')) {
      const min = this.localDate();
      min.setFullYear(min.getFullYear() - 10);
      min.setMonth(0);
      min.setDate(1);
      options.min = min.toISOString().split('T')[0];
    }

    elem.min = options.min || '';

    if (!Object.hasOwn(options, 'max')) {
      const max = this.localDate();
      max.setFullYear(max.getFullYear() + 10);
      max.setMonth(11);
      max.setDate(31);
      options.max = max.toISOString().split('T')[0];
    }

    elem.max = options.max || '';
    const dMask = dic.date.mask;
    options.type = 'text';
    options.mask = { template: 'date' };

    if (!options.prefix) {
      options.prefix = { icon: { name: 'calendar', type: 'solid' } };
    }

    elem.addEventListener('focus', (e) => {
      e.stopPropagation();
      const elem = e.currentTarget;
      if (elem.disabled || elem.readOnly) return;
      let id = elem.id;
      const value = elem.dataset.value;
      dateClass.#calendar(elem, value);
    });

    // O mesmo validador atende digitação, calendário, carga inicial e composites.
    elem.validateDate = () => {
      if (!elem.value.trim()) {
        delete elem.dataset.valid;
        delete elem.dataset.value;
        return;
      }
      const dbDate = format.dateDB(elem.value);
      const date = new Date(dbDate);
      const validIso = !isNaN(date.getTime()) && /^\d{4}-\d{2}-\d{2}$/.test(dbDate)
        && dbDate === date.toISOString().split('T')[0];
      const validMin = !elem.min || dbDate >= elem.min;
      const validMax = !elem.max || dbDate <= elem.max;
      elem.dataset.valid = validIso && validMin && validMax;
      if (elem.dataset.valid === 'true') elem.dataset.value = dbDate;
      else delete elem.dataset.value;
    };

    this.removeCalendar = (e) => {
      const calendarClick = this._calendarClick;
      this._calendarClick = false;
      if (calendarClick) return;
      document.querySelector('.calendar')?.remove();
      e.currentTarget.validateDate();
    };

    elem.addEventListener('blur', this.removeCalendar);

    elem.addEventListener('keyup', (e) => {
      const input = e.target;

      if (input.dataset.type === 'date' && input.value.length === 10) {
        const form = input.form || document;

        const fields = Array.from(form.querySelectorAll('input, select, textarea')).filter(el => {
          const isIgnoredType = el.type === 'checkbox' || el.type === 'radio';
          const isCalendarClass = Array.from(el.classList).some(cls => cls.startsWith('calendar-'));
          return !el.disabled && !isIgnoredType && !isCalendarClass;
        });

        const index = fields.indexOf(input);
        if (index > -1 && fields[index + 1]) {
          fields[index + 1].focus();
        }
      }
    });

    elem.addEventListener('keydown', (e) => {
      const input = e.target;

      if (input.dataset.type === 'date' && (e.keyCode === 9 || e.keyCode === 13)) {
        const form = input.form || document;

        const fields = Array.from(form.querySelectorAll('input, select, textarea')).filter(el => {
          const isIgnoredType = el.type === 'checkbox' || el.type === 'radio';
          const isCalendarClass = Array.from(el.classList).some(cls => cls.startsWith('calendar-'));
          return !el.disabled && !isIgnoredType && !isCalendarClass;
        });

        const index = fields.indexOf(input);
        if (index > -1 && fields[index + 1]) {
          fields[index + 1].focus();
        }
      }
    });

    return [elem, options];
  }

  /**
   * Monta o calendário para um input Date.
   *
   * O calendário respeita `elem.min`/`elem.max`, permite seleção de mês/ano,
   * destaca hoje, data selecionada, sábados, domingos e feriados fixos/móveis,
   * e emite `input` + `change` ao selecionar um dia.
   *
   * @param {HTMLInputElement} elem Input Date.
   * @param {string|null} value Data ISO usada para posicionar o calendário.
   * @returns {void}
   */
  #calendar(elem, value) {
    const today = new Date();
    const [min, max] = [elem.min || '', elem.max || ''];
    const format = new Format();
    const formatCalendarDate = (target) => {
      const targetYear = String(target.getFullYear()).padStart(4, '0');
      const targetMonth = String(target.getMonth() + 1).padStart(2, '0');
      const targetDay = String(target.getDate()).padStart(2, '0');
      return `${targetYear}-${targetMonth}-${targetDay}`;
    };
    const calendarDate = (targetYear, targetMonth, targetDay) => {
      const candidate = new Date(0);
      candidate.setHours(0, 0, 0, 0);
      candidate.setFullYear(targetYear, targetMonth, targetDay);
      return candidate;
    };
    const parseCalendarDate = (target) => {
      const match = String(target || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return match
        ? calendarDate(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
        : new Date(target);
    };
    const selectedValue = elem.data('value') || null;
    // Dentro de dialog/drawer, mantém o calendário como filho para continuar
    // dentro do escopo de foco; Popover o tira do recorte de overflow do modal.
    const dialog = elem.closest('.dialog');
    let parent = dialog || document.body;
    let date;

    if (value) {
      date = parseCalendarDate(value);
    } else {
      const minDate = min ? new Date(`${min}T00:00:00`) : null;
      const maxDate = max ? new Date(`${max}T00:00:00`) : null;

      if (minDate && maxDate) {
        if (today >= minDate && today <= maxDate) {
          date = today;
        } else if (today < minDate) {
          date = minDate;
        } else {
          date = maxDate;
        }
      } else if (minDate && today >= minDate) {
        date = today;
      } else if (maxDate && today <= maxDate) {
        date = today;
      } else if (!minDate && !maxDate) {
        date = today;
      } else if (minDate && today < minDate) {
        date = minDate;
      } else if (maxDate && today > maxDate) {
        date = maxDate;
      }
    }

    const d = new Date(date);
    d.setHours(0, 0, 0, 0);
    const firstDay = calendarDate(d.getFullYear(), d.getMonth(), 1);
    const weekDay = firstDay.getDay();
    const month = d.getMonth();
    const year = d.getFullYear();
    const daysOfMonth = calendarDate(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    let calendar, years, months, days;

    if (document.querySelectorAll('.calendar').length > 0) {
      document.querySelectorAll('.calendar').forEach(item => {
        item.remove();
      });
    }

    calendar = document.createElement('div');
    calendar.classList.add('calendar');
    const usePopover = Boolean(dialog && typeof calendar.showPopover === 'function');
    if (usePopover) calendar.setAttribute('popover', 'manual');
    if (dialog && !usePopover) parent = document.body;
    if (dialog) calendar._epPopupOwner = dialog;
    calendar.style.position = 'fixed';
    calendar.style.inset = 'auto';
    calendar.style.margin = '0';
    const dialogZIndex = dialog
      ? Number.parseInt(window.getComputedStyle(dialog).zIndex, 10)
      : 0;
    calendar.style.zIndex = String(Math.max(200, Number.isFinite(dialogZIndex) ? dialogZIndex + 1 : 200));
    calendar.addEventListener('mousedown', () => {
      this._calendarClick = true;
    });

    parent.append(calendar);

    const header = document.createElement('div');
    header.classList.add('calendar-header');

    days = document.createElement('div');
    days.classList.add('calendar-days');
    calendar.append(header, days);

    const arrowLeft = document.createElement('i');
    arrowLeft.classList.add('calendar-arrow', 'fa-solid', 'fa-caret-left');
    const u = new Date(d);
    u.setDate(0);

    if (!min || formatCalendarDate(u) >= min) {
      arrowLeft.classList.remove('calendar-arrow-disabled');
      arrowLeft.addEventListener('click', (e) => {
        d.setDate(1);
        d.setMonth(d.getMonth() - 1);
        dateClass.#calendar(elem, formatCalendarDate(d))
      });
    } else {
      arrowLeft.classList.add('calendar-arrow-disabled');
    }

    const arrowRight = document.createElement('i');
    arrowRight.classList.add('calendar-arrow', 'fa-solid', 'fa-caret-right');
    const v = new Date(d);
    v.setDate(1);
    v.setMonth(v.getMonth() + 1);

    if (!max || formatCalendarDate(v) <= max) {
      arrowRight.classList.remove('calendar-arrow-disabled');
      arrowRight.addEventListener('click', (e) => {
        d.setDate(1);
        d.setMonth(d.getMonth() + 1);

        if (min && formatCalendarDate(d) < min) {
          d.setMonth(parseInt(min.substr(5, 2), 10) - 1);
        }

        if (max && formatCalendarDate(d) > max) {
          d.setMonth(parseInt(max.substr(5, 2), 10) - 1);
        }

        dateClass.#calendar(elem, formatCalendarDate(d))
      });
    } else {
      arrowRight.classList.add('calendar-arrow-disabled');
    }

    months = document.createElement('select');
    months.id = elem.id + '-months';
    months.classList.add('calendar-months');
    months.addEventListener('change', (e) => {
      const month = e.currentTarget.value;
      d.setDate(1);
      d.setMonth(month);
      dateClass.#calendar(elem, formatCalendarDate(d))
    });

    let selected = false;
    for (const i in dic.date.monthNames) {
      const monthIndex = Number(i);
      const dmin = calendarDate(year, monthIndex + 1, 0);
      const dmax = calendarDate(year, monthIndex, 1);

      if (
        (min && max && formatCalendarDate(dmin) >= min && formatCalendarDate(dmax) <= max)
        || (min && !max && formatCalendarDate(dmin) >= min)
        || (!min && max && formatCalendarDate(dmax) <= max)
        || (!min && !max)
      ) {
        const option = document.createElement('option');
        option.value = i;
        option.textContent = dic.date.monthNames[i];
        if (monthIndex === month) {
          option.setAttribute('selected', 'selected');
          selected = true;
        }

        months.append(option);
      }
    }

    years = document.createElement('select');
    years.id = elem.id + '-years';
    const ymin = min ? parseInt(min.substr(0, 4), 10) : 1;
    const ymax = max ? parseInt(max.substr(0, 4), 10) : 9999;

    years.classList.add('calendar-years');
    years.addEventListener('change', (e) => {
      const year = parseInt(e.currentTarget.value, 10);

      // Salva o dia e mês atuais
      const currentDay = d.getDate();
      const currentMonth = d.getMonth();

      // Para evitar problemas com datas como 31 de meses curtos, zera o dia
      d.setDate(1);
      d.setFullYear(year);
      d.setMonth(currentMonth);

      // Ajusta para o dia original se possível, senão usa o último dia do mês
      const maxDay = calendarDate(d.getFullYear(), d.getMonth() + 1, 0).getDate();
      d.setDate(Math.min(currentDay, maxDay));

      // Corrige mês se estiver fora dos limites
      if (min && formatCalendarDate(d) < min) {
        d.setMonth(parseInt(min.substr(5, 2), 10) - 1);
      }

      if (max && formatCalendarDate(d) > max) {
        d.setMonth(parseInt(max.substr(5, 2), 10) - 1);
      }

      dateClass.#calendar(elem, formatCalendarDate(d));
    });


    for (let i = ymax; i >= ymin; i--) {
      const option = document.createElement('option');
      option.value = i;
      option.textContent = i;
      if (i === year) {
        option.setAttribute('selected', 'selected');
      }
      years.append(option);
    }

    header.append(arrowLeft, months, years, arrowRight);

    years.value = year;
    months.value = month;

    let row = 1;
    let col = 1;
    const names = dic.date.uniqueDayNames;

    for (const i in names) {
      const day = document.createElement('div');
      day.classList.add('calendar-week-title');
      day.textContent = names[i];
      col++;
      days.append(day);
    }

    row++;
    col = weekDay + 1;
    const holidays = dateClass.#getHolidays(year);

    for (let i = 1; i <= daysOfMonth; i++) {
      const day = document.createElement('div');
      const formattedMonth = String(month + 1).padStart(2, '0');
      const formattedDay = String(i).padStart(2, '0');
      const formattedYear = String(year).padStart(4, '0');
      const formattedDate = `${formattedYear}-${formattedMonth}-${formattedDay}`;

      day.data('value', formattedDate);
      day.textContent = i;
      day.style.gridRowStart = row;
      day.style.gridColumnStart = col;

      if (col === 1) {
        day.classList.add('ep-day-sunday');
      }

      if (col === 7) {
        day.classList.add('ep-day-saturday');
      }

      if (day.data('value') === format.dateDB(this.localDate())) {
        day.classList.add('ep-day-today');
      }

      if (holidays.hasOwnProperty(formattedDate)) {
        day.title = holidays[formattedDate];
        day.classList.add('ep-day-holiday');
      }

      if (formattedDate === selectedValue) {
        day.classList.add('ep-day-selected');
      }

      col++;

      if (col === 8) {
        col = 1;
        row++;
      }
      days.append(day);

      if ((!min || formattedDate >= min) && (!max || formattedDate <= max)) {
        day.classList.add('ep-day');
        day.addEventListener('click', (e) => {
          const day = e.currentTarget;
          const calendar = day.parentElement.parentElement;
          const field = elem;//calendar.parentElement.querySelector('input');
          const value = day.data('value');
          field.data('value', value);
          field.value = format.date(value);
          field.dispatchEvent(new Event('input', { bubbles: true }));
          field.dispatchEvent(new Event('change', { bubbles: true }));
          calendar.remove();
        });
      } else {
        day.classList.add('ep-day-disabled');
      }
    }

    if (usePopover) calendar.showPopover();

    // O popup usa coordenadas da viewport para escapar ao overflow dos dialogs.
    const inputRect = elem.parentElement.getBoundingClientRect();
    const calendarRect = calendar.getBoundingClientRect();
    const calendarHeight = calendarRect.height || calendar.offsetHeight;
    const calendarWidth = calendarRect.width || calendar.offsetWidth;
    const viewportPadding = 8;
    const spaceBelow = window.innerHeight - inputRect.bottom - viewportPadding;

    let top = spaceBelow >= calendarHeight || inputRect.top < calendarHeight + viewportPadding
      ? inputRect.bottom
      : inputRect.top - calendarHeight;
    top = Math.max(
      viewportPadding,
      Math.min(top, window.innerHeight - calendarHeight - viewportPadding)
    );

    const left = Math.max(
      viewportPadding,
      Math.min(inputRect.left, window.innerWidth - calendarWidth - viewportPadding)
    );

    // Aplica os estilos ao calendário
    calendar.style.top = `${top}px`;
    calendar.style.left = `${left}px`;

    // Função para remover calendário no scroll
    /** Remove o calendário em scroll externo. @returns {void} */
    const scrollHandler = () => {
      const calendarClick = this._calendarClick;
      this._calendarClick = false;

      if (calendarClick) return;

      const calendar = document.querySelector('.calendar');
      if (calendar) {
        calendar.remove();
        window.removeEventListener('scroll', scrollHandler, true); // Limpa o listener
      }
    };

    // Adiciona o listener com capture true (para pegar scrolls em qualquer container)
    window.addEventListener('scroll', scrollHandler, true);

  }

  /**
   * Combina feriados fixos do dicionário com feriados móveis gregorianos.
   *
   * @param {number} year Ano.
   * @returns {Object<string,string>} Mapa ordenado `YYYY-MM-DD -> nome`.
   */
  #getHolidays(year) {
    const fixedHolidays = {};
    for (const i in dic.date.holidays) {
      fixedHolidays[`${year}-${i}`] = dic.date.holidays[i];
    }

    const movableHolidays = dateClass.#gregorianHolidays(year);
    const holidays = { ...movableHolidays, ...fixedHolidays };
    return Object.keys(holidays)
      .sort() // Ordena as chaves
      .reduce((acc, key) => {
        acc[key] = holidays[key]; // Reconstrói o objeto ordenado
        return acc;
      }, {});
  }

  /**
   * Calcula Carnaval, Sexta-feira Santa, Páscoa e Corpus Christi.
   *
   * @param {number} year Ano gregoriano.
   * @returns {Object<string,string>} Mapa de feriados móveis.
   */
  #gregorianHolidays(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31); // Mês da Páscoa
    const day = ((h + l - 7 * m + 114) % 31) + 1; // Dia da Páscoa

    const easter = new Date(year, month - 1, day);

    // Calculando outros feriados móveis
    const carnival = new Date(easter);
    carnival.setDate(easter.getDate() - 47); // 47 dias antes da Páscoa

    const goodFriday = new Date(easter);
    goodFriday.setDate(easter.getDate() - 2); // 2 dias antes da Páscoa

    const corpusChristi = new Date(easter);
    corpusChristi.setDate(easter.getDate() + 60); // 60 dias após a Páscoa

    const names = dic.date.holidayNames;
    return {
      [carnival.toISOString().substr(0, 10)]: names.carnival,
      [corpusChristi.toISOString().substr(0, 10)]: names.corpusChristi,
      [easter.toISOString().substr(0, 10)]: names.easter,
      [goodFriday.toISOString().substr(0, 10)]: names.goodFriday
    };
  }

  /**
   * Cria/converte uma data para o tratamento local usado pelo calendário.
   *
   * @param {Date|string} [input] Data existente ou string parseável.
   * @returns {Date} Objeto Date ajustado conforme a lógica vigente.
   */
  localDate(input) {
    let date;

    // Verifica o tipo de entrada e cria o objeto Date
    if (input instanceof Date) {
      date = input;
    } else if (typeof input === 'string') {
      date = new Date(input);
    } else {
      date = new Date(); // Se nada for passado, usa a data atual
    }

    // Verifica se a data já está ajustada para o fuso horário local
    const isLocalTime = date.getTime() === new Date(date.toISOString()).getTime();
    if (!isLocalTime) {
      // Ajusta para a hora local apenas se necessário
      const tzOffset = date.getTimezoneOffset() * 60000;
      date = new Date(date.getTime() - tzOffset);
    }
    return date;
  }
}

const dateClass = new DateClass();

/**
 * Factory do Date para o pipeline Input.
 *
 * @param {HTMLInputElement} elem Input base.
 * @param {Object} options Opções recebidas pelo componente; veja {@link DateClass#date}.
 * @returns {Array.<HTMLInputElement|Object>} Input e opções normalizadas.
 * @see DateClass#date
 */
const date = (elem, options) => dateClass.date(elem, options);
export default date;

/**
 * Helper público para obter a data conforme a lógica local do Date EP.
 *
 * @param {Date|string} [input] Data.
 * @returns {Date} Data ajustada.
 * @see DateClass#localDate
 */
const localDate = (...args) => dateClass.localDate(...args);
export { localDate };
